import { useState } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import { AccessDenied, ChannelFilterChips, isoDayOf, lastMonth } from './shared'
import type { OrderSource } from './shared'
import ReconciliationTab from './ReconciliationTab'
import PlanningTab from './PlanningTab'
import ProductDrillPanel from './ProductDrillPanel'
import type { DrillTarget } from './ProductDrillPanel'

type Tab = 'reconciliation' | 'planning'

const TABS: { id: Tab; label: string }[] = [
  { id: 'reconciliation', label: 'Reconciliation' },
  { id: 'planning', label: 'Planning' },
]

const ProductReportsPage = () => {
  const { hasPermission } = usePermissions()

  // Lifted so switching tabs (or opening the drill) never loses the selection.
  const [activeTab, setActiveTab] = useState<Tab>('reconciliation')
  const [channel, setChannel] = useState<OrderSource | null>(null)
  const [range, setRange] = useState(() => {
    const [start, end] = lastMonth()
    return { start, end }
  })
  const [isoDay, setIsoDay] = useState(() => isoDayOf(new Date()))
  const [drill, setDrill] = useState<DrillTarget | null>(null)

  // Both permissions are required: the server silently floors the lower list
  // tiers to the last 7 days, so a page over an arbitrary month needs
  // order.list.all to get the numbers it claims to show.
  const canView =
    hasPermission(PERMISSIONS.STATS_PRODUCT_SALES_VIEW) && hasPermission(PERMISSIONS.ORDER_LIST_ALL)

  if (!canView) return <AccessDenied message="You don't have permission to view product reports." />

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Product Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          What was made — reconciliation over a period, patterns per weekday
        </p>
      </div>

      <ChannelFilterChips value={channel} onChange={setChannel} />

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Only the active tab is mounted — each tab fetches its own document lazily. */}
      {activeTab === 'reconciliation' && (
        <ReconciliationTab
          channel={channel}
          startDate={range.start}
          endDate={range.end}
          onStartChange={(start) => setRange((r) => ({ ...r, start }))}
          onEndChange={(end) => setRange((r) => ({ ...r, end }))}
          onOpenDrill={setDrill}
        />
      )}
      {activeTab === 'planning' && (
        <PlanningTab
          channel={channel}
          isoDay={isoDay}
          onIsoDayChange={setIsoDay}
          onOpenDrill={setDrill}
        />
      )}

      {drill && <ProductDrillPanel drill={drill} channel={channel} onClose={() => setDrill(null)} />}
    </div>
  )
}

export default ProductReportsPage
